import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from "framer-motion";

/**
 * @typedef {Object} NavLink
 * @property {string} name - El texto visible del enlace.
 * @property {string} href - La URL o el ID de la sección a la que apunta el enlace.
 * @property {boolean} [external=false] - Indica si el enlace es externo y debe abrirse en una nueva pestaña.
 */

/**
 * `NavbarLanding` es un componente de barra de navegación para una landing page.
 * Incorpora mejoras estéticas para una apariencia más moderna y responsiva.
 *
 * Mejoras estéticas y de diseño:
 * - Diseño responsivo con menú hamburguesa para pantallas pequeñas.
 * - Sutiles efectos hover para una mejor interactividad.
 * - Iconos (ej. usando Heroicons o similar) para el botón del menú móvil.
 * - Ajuste de espaciado y tamaño para un look más limpio.
 */
const NavbarLanding = () => {
  // Estado para controlar la visibilidad del menú móvil (menú hamburguesa)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  // CAMBIO 1: Estado para detectar el scroll
  const [scrolled, setScrolled] = useState(false);

  // CAMBIO 2: useEffect para detectar scroll y aplicar la clase
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Características', href: '#features' },
    { name: 'Precios', href: '#pricing' },
    { name: 'Contacto', href: '#contact' },
  ];

  const handleLogin = () => {

    navigate('/auth');  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    // CAMBIO 3: Aplicamos estilos condicionales y el efecto de blur
    <nav className={`
      sticky top-0 z-50 w-full border-b backdrop-blur-lg
      transition-all duration-300
      ${scrolled
        ? "bg-gray-900/80 dark:bg-gray-900/80 border-gray-800 dark:border-gray-800"
        : "bg-transparent border-transparent"
      }
    `}>
      <div className="container mx-auto px-4 py-3 flex justify-between items-center relative">
        {/* Logo o Título */}
        <Link to="/">
                  <img src="/AgendateHoy-logo-dark.webp" alt="AgendateHoy" className="h-14  w-auto" />
                </Link>

        {/* Botón de Menú Hamburguesa para móviles */}
        <div className="md:hidden"> {/* Visible solo en pantallas pequeñas */}
          <button
            onClick={toggleMobileMenu}
            className="text-white hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-white p-2 rounded-md transition-colors duration-200"
            aria-label="Toggle navigation"
          >
            {/* Ícono de hamburguesa o de cierre */}
            {isMobileMenuOpen ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"></path></svg>
            )}
          </button>
        </div>

        {/* Enlaces de Navegación (ocultos en móvil por defecto, visibles en desktop) */}
        <ul className="hidden md:flex space-x-8 items-center"> {/* Hidden en móvil, flex en desktop */}
          {navLinks.map((link) => (
            <NavLinkItem key={link.href} link={link} />
          ))}
        </ul>

        {/* Botón de Acción (visible en desktop) */}
        <button
          onClick={handleLogin}
          className="hidden md:block bg-white text-gray-800 px-6 py-2 rounded-full font-semibold shadow-md hover:bg-gray-200 hover:scale-105 transition-all duration-300 ease-in-out" // Estilo más moderno
        >
          Iniciar Sesión
        </button>
      </div>

      {/* Menú Móvil Desplegable */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-gray-900 absolute w-full py-4 shadow-lg animate-fade-in-down"> {/* Animación */}
          <ul className="flex flex-col items-center space-y-4">
            {navLinks.map((link) => (
              <NavLinkItem key={link.href} link={link} onClick={() => setIsMobileMenuOpen(false)} /> 
            ))}
            <li>
              <button
                onClick={() => { handleLogin(); setIsMobileMenuOpen(false); }}
                className="w-full bg-white text-gray-800 px-6 py-2 rounded-full font-semibold shadow-md hover:bg-gray-200 transition-colors duration-300"
              >
                Iniciar Sesión
              </button>
            </li>
          </ul>
        </div>
      )}
    </nav>
  );
};

// Componente auxiliar para los elementos de la lista de navegación
/**
 * `NavLinkItem` es un componente de ayuda para renderizar un enlace de navegación.
 * @param {Object} props
 * @param {NavLink} props.link - Objeto que contiene los detalles del enlace.
 * @param {Function} [props.onClick] - Función opcional para ejecutar al hacer clic (ej. cerrar menú móvil).
 */
const NavLinkItem = ({ link, onClick }) => {
  const isExternal = link.external;

  return (
    <li>
      <a
        href={link.href}
        // Clase para enlaces: más padding, texto más grande y efecto hover más pronunciado
        className="block px-3 py-2 text-lg font-medium text-white hover:bg-gray-700 rounded-md transition-all duration-300 ease-in-out"
        target={isExternal ? '_blank' : '_self'}
        rel={isExternal ? 'noopener noreferrer' : undefined}
        onClick={onClick} // Pasa el onClick para cerrar el menú móvil
      >
        {link.name}
      </a>
    </li>
  );
};

export default NavbarLanding;